import { useState, useEffect, useRef, useCallback } from 'react';
import { useFlags as useLDFlags } from 'gatsby-plugin-launchdarkly';
import { createEngagementClient } from '../../../../src/services/client';
import { 
  GET_WORKOUT_COMMENTS, 
  CREATE_WORKOUT_COMMENT, 
  DELETE_WORKOUT_COMMENT 
} from '../engagementGql/comments';
import { addErrorInDatadogRum } from '../utils/utilities';
import { 
  getWorkoutCommentErrorMessage, 
  performWorkoutCommentCascadeDelete, 
  processWorkoutComments,
  processPaginatedWorkoutComments
} from '../utils/workoutCommentsHelpers';
import { Comment, UseNewWorkoutCommentsProps } from '../types/workoutComments';
import { USER_DISPLAY } from '../constants/workout';

const useNewWorkoutComments = ({ 
  workoutName, 
  givenName, 
  familyName, 
  workoutPageData 
}: UseNewWorkoutCommentsProps) => {
  const { rtNewWorkoutComments } = useLDFlags();
  
  const [comments, setComments] = useState<Comment[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loadingNextPage, setLoadingNextPage] = useState<boolean>(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Throttle mechanism for scroll handler
  const scrollThrottleRef = useRef(false);
  const pageSize = 20;

  // Load initial comments
  const loadComments = async (): Promise<void> => {
    if (!rtNewWorkoutComments) return;
    
    try {
      const response = await createEngagementClient.query({
        query: GET_WORKOUT_COMMENTS,
        variables: {
          workoutId: workoutName,
          ...(pageSize && { pageSize }),
        },
      });

      if (response?.data?.getWorkoutCommentList) {
        const responseData = response.data.getWorkoutCommentList;
        const items = responseData.items || [];
        const topLevelComments = processWorkoutComments(items);
        
        setComments(topLevelComments);
        setNextToken(responseData.nextToken || null);
      }
    } catch (err: any) {
      addErrorInDatadogRum(err);
    }
  };

  // Load more comments (pagination)
  const loadMoreComments = useCallback(async () => {
    if (!rtNewWorkoutComments) return;
    
    setLoadingNextPage(true);
    if (!nextToken) {
      setLoadingNextPage(false);
      return;
    }

    try {
      const newData = await createEngagementClient.query({
        query: GET_WORKOUT_COMMENTS,
        variables: {
          workoutId: workoutName,
          nextToken,
          ...(pageSize && { pageSize }),
        },
      });

      if (newData?.data?.getWorkoutCommentList) {
        const responseData = newData.data.getWorkoutCommentList;
        setNextToken(responseData.nextToken || null);
        const newItems = responseData.items || [];
        
        if (newItems.length > 0) {
          setComments((prevComments: Comment[]) => {
            const topLevelNewItems = processPaginatedWorkoutComments(newItems, prevComments);
            return [...prevComments, ...topLevelNewItems];
          });
        }
      }
    } catch (loadmoreError: any) {
      addErrorInDatadogRum(loadmoreError);
    } finally {
      setLoadingNextPage(false);
    }
  }, [rtNewWorkoutComments, nextToken, workoutName, pageSize]);

  // Create new comment (NEW API only)
  const createComment = async (content: string) => {
    if (!rtNewWorkoutComments) return;
    
    // Basic validation
    if (!content.trim()) {
      setErrorMessage('Comment cannot be empty');
      setIsErrorDialogOpen(true);
      return;
    }
    
    const sanitizedContent = content.trim();
    const tempCommentId = `temp_${Date.now()}_${Math.random()}`;
    const newComment = {
      id: tempCommentId,
      user: { name: `${givenName} ${familyName}`.trim() || USER_DISPLAY.ANONYMIZED_USER_NAME },
      timestamp: new Date().toISOString(),
      content: sanitizedContent, // Use sanitized content
    };
    
    setComments((prevComments: Comment[]) => [newComment, ...prevComments]);

    try {
      const response = await createEngagementClient.mutate({
        mutation: CREATE_WORKOUT_COMMENT,
        variables: {
          workoutId: workoutName,
          content: sanitizedContent, // Send sanitized content to backend
        },
      });

      if (response?.data?.createWorkoutComment) {
        const createdComment = response.data.createWorkoutComment;
        
        setComments((prevComments: Comment[]) =>
          prevComments.map((c: Comment) =>
            c.id === tempCommentId
              ? {
                  id: createdComment.id,
                  parentId: createdComment.parentId,
                  user: createdComment.user,
                  content: createdComment.content,
                  createdAt: createdComment.createdAt,
                  status: createdComment.status,
                  isReplied: false,
                  repliedComments: [],
                }
              : c
          )
        );
      }
    } catch (err: any) {
      addErrorInDatadogRum(err);
      const errorMsg = getWorkoutCommentErrorMessage(err, 'create', workoutPageData);
      setErrorMessage(errorMsg);
      setIsErrorDialogOpen(true);
    }
  };

  // Create reply to comment (NEW API only)
  const createReply = async (content: string, parentComment: Comment) => {
    if (!rtNewWorkoutComments) return;
    
    // Basic validation
    if (!content.trim()) {
      setErrorMessage('Reply cannot be empty');
      setIsErrorDialogOpen(true);
      return;
    }
    
    const sanitizedContent = content.trim();
    const realParentId = parentComment.id;
    const tempReplyId = `temp_${Date.now()}_${Math.random()}`;

    const updatedComments = comments.map((existingComment: Comment) => {
      if (existingComment.id === realParentId) {
        const newReply = {
          id: tempReplyId,
          user: { name: `${givenName} ${familyName}`.trim() || USER_DISPLAY.ANONYMIZED_USER_NAME },
          timestamp: new Date().toISOString(),
          content: sanitizedContent, // Use sanitized content
        };
        return {
          ...existingComment,
          repliedComments: existingComment.repliedComments
            ? [...existingComment.repliedComments, newReply]
            : [newReply],
          isReplied: true,
        };
      }
      return existingComment;
    });
    
    setComments(updatedComments);

    try {
      const response = await createEngagementClient.mutate({
        mutation: CREATE_WORKOUT_COMMENT,
        variables: {
          workoutId: workoutName,
          content: sanitizedContent, // Send sanitized content to backend
          replyTo: realParentId,
        },
      });

      if (response?.data?.createWorkoutComment) {
        const createdReply = response.data.createWorkoutComment;

        setComments((prevComments: Comment[]) =>
          prevComments.map((existingComment: Comment) => {
            if (existingComment.id === realParentId) {
              return {
                ...existingComment,
                repliedComments:
                  existingComment.repliedComments?.map((reply: Comment) =>
                    reply.id === tempReplyId
                      ? {
                          id: createdReply.id,
                          parentId: createdReply.parentId,
                          user: createdReply.user,
                          content: createdReply.content,
                          createdAt: createdReply.createdAt,
                          status: createdReply.status,
                        }
                      : reply
                  ) || [],
              };
            }
            return existingComment;
          })
        );
      }
    } catch (err: any) {
      addErrorInDatadogRum(err);
      const errorMsg = getWorkoutCommentErrorMessage(err, 'create', workoutPageData);
      setErrorMessage(errorMsg);
      setIsErrorDialogOpen(true);
    }
  };

  // Delete comment
  const deleteComment = async (commentId: string) => {
    if (!rtNewWorkoutComments) return { success: false };
    
    try {
      await createEngagementClient.mutate({
        mutation: DELETE_WORKOUT_COMMENT,
        variables: {
          workoutId: workoutName,
          id: commentId,
        },
      });
      
      const updatedComments = performWorkoutCommentCascadeDelete(comments, commentId);
      setComments(updatedComments);
      
      return { success: true };
    } catch (err: any) {
      addErrorInDatadogRum(err);
      const errorMsg = getWorkoutCommentErrorMessage(err, 'delete', workoutPageData);
      setErrorMessage(errorMsg);
      setIsErrorDialogOpen(true);
      
      return { success: false, error: err };
    }
  };

  // Smooth infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!rtNewWorkoutComments) return;
    
    // Ultra-responsive throttle for buttery smooth experience
    if (scrollThrottleRef.current) return;
    scrollThrottleRef.current = true;
    
    // Use requestAnimationFrame for 60fps smoothness
    requestAnimationFrame(() => {
      scrollThrottleRef.current = false;
    });
    
    // Ultra-aggressive bottom detection for seamless UX
    const scrollPosition = window.innerHeight + window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;
    const threshold = 1500; // Huge threshold - trigger way before user reaches bottom
    
    if (scrollPosition >= documentHeight - threshold) {
      // Only load more if we're not already loading AND there are more comments to load
      if (!loadingNextPage && nextToken) {
        loadMoreComments();
      }
    }
  }, [rtNewWorkoutComments, loadingNextPage, nextToken, loadMoreComments]);

  // Setup scroll listener
  useEffect(() => {
    if (rtNewWorkoutComments) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [rtNewWorkoutComments, handleScroll]);

  const closeErrorDialog = () => {
    setIsErrorDialogOpen(false);
    setErrorMessage('');
  };

  return {
    // State
    comments,
    nextToken,
    loadingNextPage,
    isErrorDialogOpen,
    errorMessage,
    rtNewWorkoutComments,
    
    // Actions
    loadComments,
    loadMoreComments,
    createComment,
    createReply,
    deleteComment,
    closeErrorDialog,
  };
};

export default useNewWorkoutComments;
